/* ========================================================================= *
 * THE CHAMPION OF RECORD IS THE SCHOOL. The athletes are listed under it.
 *
 * Brian Kass, 2026-10-19:
 *
 *   "Individual championships are NOT split. The champion of record is the
 *    SCHOOL, and the individual athlete and event are attached as the
 *    reference." · "Outreach goes to the school's AD and head coach, names
 *    the athlete, and arrives with a solution (the ring offer)."
 *
 * 🔴 THIS REPLACES THE "School — Person" RENDER COMMITTED EARLIER TODAY
 * (6148e56). That version put the person in `champion` and the school in
 * `winningSchool` and rendered them as one joined string. The ruling above
 * makes the school the champion of record outright — so `champion` now holds
 * a school on BOTH kinds of title, and the athletes are a separate list.
 *
 * WHY THAT IS SIMPLER AND NOT JUST DIFFERENT: the 40 existing
 * `champion || winningSchool` call sites across 21 files were all reaching for
 * the account. Under this ruling `champion` IS the account, on every title, so
 * those sites become correct without being touched. The fallback is not
 * removed by editing 40 places; it is made harmless by fixing what the field
 * means.
 *
 * ⚠️ WHAT STILL NEEDS THIS MODULE, then: the ATHLETES. They are a list, they
 * live in a JSON textarea, and the parse must never turn a malformed value
 * into "no athletes" — which is the one new way this design can lie.
 *
 * Mirrors individual-champion.js on the backend. If one changes, change both.
 * ========================================================================= */

const S = (v) => (v == null ? '' : String(v)).trim()

// Display-only alias comparison, never assignment/account identity authority.
// Arbitrary substring containment does not identify a school.
const schoolAliasKey = (value) => S(value).toLowerCase()
  .replace(/[.']/g, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
  .replace(/\s+(?:high school|hs)$/, '').trim()
export const sameSchoolAlias = (a, b) => Boolean(schoolAliasKey(a)) && schoolAliasKey(a) === schoolAliasKey(b)


/* §3a — all seven spellings. A rule keyed to "(Individual)" catches 17 of 156
 * live records; the object spells it seven ways (Individual 83 · Singles 25 ·
 * Doubles 20 · (Individual) 17 · All-Around 7 · (Singles) 2 · (Doubles) 2,
 * measured 2026-10-16). Read off the RECORD's own name and key, never off the
 * champion value: a boat and a dressage horse both read like people, and 100
 * of the 230 individual records are uncrowned with no name to read at all
 * (measured 2026-10-19). */
const INDIVIDUAL_MARKS = [
  /\((?:Individual|Singles|Doubles)\)/i,
  /\bindividual\b/i,
  /\bsingles\b/i,
  /\bdoubles\b/i,
  /\ball-?around\b/i,
  /\bmedalist\b/i,
  /\bno\.?\s*[1-9]\b/i,
  /\bweight\s*class\b/i,
  /\b\d{2,3}\s*(?:lbs?|pounds)\b/i,
]

/** 'individual' | 'team' | 'unknown'. `unknown` never tightens anything. */
export function titleTypeOf(c) {
  const hay = [S(c?.name), S(c?.championshipName), S(c?.key), S(c?.championshipKey)].join(' | ')
  if (!hay.replace(/\s*\|\s*/g, '')) return 'unknown'
  for (const re of INDIVIDUAL_MARKS) if (re.test(hay)) return 'individual'
  return 'team'
}

/* §3b — not every individual title HAS a school. Measured 2026-10-19: of 130
 * crowned individual titles, 13 sit on a non-school segment (Equestrian 12,
 * Pro Individual/Tour 1) where the ATHLETE is correctly the champion of
 * record. A vaulter has a club; a tour professional has neither.
 *
 * The UI needs this as much as the write path: without it the board would
 * draw an athlete-shaped champion as a data defect on 13 correct records. */
const SCHOOL_SEGMENTS = new Set(['HS State', 'College', 'Conference', 'Canada'])

export function schoolApplies(c) {
  const seg = S(c?.segment) || S(c?.srSegment) || S(c?.sr_segment)
  return seg ? SCHOOL_SEGMENTS.has(seg) : false
}

export const BLOB_SHAPE = /^(.*\S)\s*\(([^()]{2,80})\)\s*$/
export function looksBlobbed(v) { return BLOB_SHAPE.test(S(v)) }

/* ========================================================================= *
 * THE ATHLETES.
 *
 * ⚠️ NEVER RETURNS AN EMPTY LIST FOR A BROKEN VALUE, and this is the whole
 * reason the parse is a function rather than a JSON.parse at each call site.
 * `champion_references` is a hand-editable textarea in HubSpot, so malformed
 * JSON will happen. "No athletes recorded" and "athletes recorded and
 * unreadable" are different facts: the first is a gap to fill, the second is a
 * data defect to fix. Collapsing them turns a defect into a silent zero, which
 * is the single most repeated failure in this system's history.
 * ========================================================================= */
export function parseReferences(raw) {
  const s = S(raw)
  if (!s) return { ok: true, refs: [], raw: s, error: null }
  let parsed
  try { parsed = JSON.parse(s) }
  catch (e) { return { ok: false, refs: [], raw: s, error: 'unreadable: ' + String(e?.message || e) } }
  if (!Array.isArray(parsed)) return { ok: false, refs: [], raw: s, error: 'not a list' }
  if (parsed.some((x) => !x || typeof x !== 'object' || Array.isArray(x)
    || ['athlete', 'event', 'school', 'source'].some((key) => x[key] != null && typeof x[key] !== 'string'))) {
    return { ok: false, refs: [], raw: s, error: 'invalid reference entry' }
  }
  return { ok: true, refs: parsed, raw: s, error: null }
}

/**
 * The athletes to list beneath the school.
 *
 * Accepts either the raw textarea (board records, loaded in bulk) or an
 * already-parsed array (the per-record route, which parses server-side), so a
 * consumer does not have to know which shape it has.
 */
export function athletesOf(c) {
  if (S(c?.athletesError)) return { ok: false, error: S(c.athletesError), lines: [], raw: S(c?.athletesRaw) || null }
  const refs = Array.isArray(c?.references) ? c.references : Array.isArray(c?.athletes) ? c.athletes : null
  if (refs) {
    if (refs.some((x) => !x || typeof x !== 'object' || Array.isArray(x)
      || ['athlete', 'event', 'school', 'source'].some((key) => x[key] != null && typeof x[key] !== 'string'))) {
      return { ok: false, error: 'invalid reference entry', lines: [], raw: null }
    }
    return { ok: true, error: null, lines: refs.map(line), raw: null }
  }
  const p = parseReferences(c?.championReferences ?? c?.champion_references)
  return p.ok
    ? { ok: true, error: null, lines: p.refs.map(line), raw: p.raw }
    : { ok: false, error: p.error, lines: [], raw: p.raw }
}

function line(r) {
  const athlete = S(r?.athlete)
  const event = S(r?.event)
  return {
    athlete,
    event,
    school: S(r?.school) || null,
    source: S(r?.source) || null,
    /* The athlete and the event, in the order the outreach names them:
     * "Congrats on [athlete]'s [event] title". */
    label: [athlete, event].filter(Boolean).join(' — '),
  }
}

/* ========================================================================= *
 * RENDERING.
 * ========================================================================= */

/* ========================================================================= *
 * 🔴 MULTI-SCHOOL — NAME NO SINGLE CHAMPION.
 *
 * Brian Kass, 2026-10-19: "Multi-school records: name no single champion.
 * Render them flagged 'multi-school' with each school listed and its athletes
 * under it, plus a CTA to resolve. Don't guess."
 *
 * A championship genuinely won by several schools — a wrestling meet across
 * six weight classes, say — has no one account. The backend writes no
 * `current_champion` for these and tags them `multi_school_unresolved`, so the
 * absent champion here is the CORRECT state and must never render as a gap in
 * the hunt. It renders as a decision waiting for a person, with the schools
 * listed and a CTA that resolves it.
 * ========================================================================= */
export const MULTI_SCHOOL_TAG = 'multi_school_unresolved'

/** Read off what the record holds — the tag, or athletes from several schools. */
export function isMultiSchool(c) {
  if (S(c?.championSearchTag) === MULTI_SCHOOL_TAG || S(c?.championSearch) === MULTI_SCHOOL_TAG || S(c?.sr_champion_search) === MULTI_SCHOOL_TAG) return true
  if (c?.multiSchool === true) return true
  const a = athletesOf(c)
  if (!a.ok) return false
  const set = new Set(a.lines.map((l) => schoolAliasKey(l.school)).filter(Boolean))
  const champ = S(c?.champion)
  if (champ) set.add(schoolAliasKey(champ))
  return set.size > 1
}

/** Each school, with its own athletes under it. The one grouping, shared with the export. */
export function schoolGroups(c) {
  if (Array.isArray(c?.schoolGroups)) return { ok: true, error: null, groups: c.schoolGroups }
  const a = athletesOf(c)
  if (!a.ok) return { ok: false, error: a.error, groups: [] }
  const champ = S(c?.champion)
  const by = new Map()
  for (const l of a.lines) {
    const school = l.school || champ || '(school not stated)'
    if (!by.has(school)) by.set(school, [])
    by.get(school).push(l)
  }
  return { ok: true, error: null,
    groups: [...by.entries()].map(([school, athletes]) => ({ school, athletes })) }
}

/**
 * The champion of record. One line, and on a school title it is the school.
 *
 * ⚠️ RETURNS '' ON A MULTI-SCHOOL RECORD, deliberately. Falling back to
 * `champion || winningSchool` here would surface whichever school happened to
 * be stored first as THE champion — the guess the ruling forbids, arriving
 * through the render instead of through the write. The row shows the
 * multi-school flag and the grouped schools instead.
 */
export function championOfRecord(c) {
  if (isMultiSchool(c)) return ''
  return S(c?.champion) || S(c?.winningSchool) || ''
}

/** Kept as the name the board already imports. The school, not a joined string. */
export function championDisplay(c) {
  return championOfRecord(c)
}

/** Every distinct school this crown touches. The buying-committee step reads this. */
export function championSchools(c) {
  if (Array.isArray(c?.schools)) return c.schools.map(S).filter(Boolean)
  const out = new Set()
  const champ = championOfRecord(c)
  if (champ && (titleTypeOf(c) !== 'individual' || schoolApplies(c))) out.add(champ)
  for (const l of athletesOf(c).lines) if (l.school) out.add(l.school)
  return [...out]
}

/**
 * Does this record satisfy the ruling? An uncrowned record is not a violation.
 *
 * `false` with a champion present is a REAL FINDING — a crown still in the
 * retired blob shape, or athletes that cannot be read — not a rendering
 * problem. 104 stored crowns were blobbed when this was written
 * (measured 2026-10-19).
 */
export function meetsContract(c) {
  if (titleTypeOf(c) !== 'individual') return true
  const a = athletesOf(c)
  /* A multi-school record with no champion of record MEETS the contract — it
   * is awaiting a human decision, not missing data. Listing it as a violation
   * would invite somebody to "fix" it by naming one of the schools, which is
   * the one thing the ruling forbids. */
  if (isMultiSchool(c)) {
    if (!a.ok) return false
    return a.lines.length > 0 && a.lines.every((l) => l.athlete && l.event && l.school)
  }
  const champ = championOfRecord(c)
  if (!champ) return true
  if (looksBlobbed(champ)) return false
  if (!schoolApplies(c)) return true
  if (!a.ok) return false
  return a.lines.length > 0 && a.lines.every((l) => l.athlete && l.event)
}

/** The account to look a crest, school record or head coach up by. '' when unknown. */
export function accountSchool(c) {
  if (titleTypeOf(c) === 'individual' && !schoolApplies(c)) return ''
  const champ = championOfRecord(c)
  return looksBlobbed(champ) ? '' : champ
}
